import { RefObject, useEffect, useRef, useState } from "react"
import { useWidget } from "../../../../providers/widget"
import { useExplorer } from "../../providers"
import { ExploreWidgetConfig } from "../../config"
import { MapChevronsIcon, MapGlyphIcon } from "./MapChrome"

/**
 * "Show map" edge tab (slice E / SBP-9, #928) — port of the toolkit
 * map-controls.js show-tab. Rendered by ExplorerContent ONLY while the map
 * is hidden: the map component unmounts on hide, so the restore control
 * cannot live inside it. position:fixed, flush to the window edge the map
 * collapsed toward (mirrored chevrons + a map glyph), at the collapse
 * button's viewport Y — max(container top, sticky offset) + 16, clamped to
 * the container's bottom so the tab never outlives the explorer on screen.
 * The sticky offset is config-derived (viewport-fit top offset, else the
 * stylesheet's top:0) — the same value the sidebar's sticky CSS is built
 * from in useExplorerCss, so no DOM measurement of the unmounted sidebar is
 * needed. An IntersectionObserver gates display to while the explorer
 * intersects the viewport; environments without IO stay in-view (module
 * fallback). Desktop-only via CSS — below 960px the stacked mobile layout
 * keeps the results-header text toggle instead. Slide-in is transform-only
 * and respects prefers-reduced-motion.
 */
export default function MapShowTab({ containerRef }: { containerRef: RefObject<HTMLDivElement | null> }) {
  const { options } = useWidget<ExploreWidgetConfig>()
  const { toggleMap } = useExplorer()
  const tabRef = useRef<HTMLButtonElement>(null)
  const [inView, setInView] = useState(true)

  const vf = options.map?.viewportFit
  const stickyTop = vf?.enabled && typeof vf.topOffsetPx === 'number' ? vf.topOffsetPx : 0

  useEffect(() => {
    const container = containerRef.current
    if (!container || !('IntersectionObserver' in window)) return
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) setInView(entry.isIntersecting)
    })
    io.observe(container)
    return () => io.disconnect()
  }, [containerRef])

  useEffect(() => {
    const container = containerRef.current
    const tab = tabRef.current
    if (!container || !tab) return
    let ticking = false
    const position = () => {
      ticking = false
      const rect = container.getBoundingClientRect()
      const top = Math.min(Math.max(rect.top, stickyTop) + 16, rect.bottom - 60)
      tab.style.top = `${top}px`
    }
    const requestPos = () => {
      if (!ticking) {
        ticking = true
        window.requestAnimationFrame(position)
      }
    }
    window.addEventListener('scroll', requestPos, { passive: true })
    window.addEventListener('resize', requestPos, { passive: true })
    position()
    return () => {
      window.removeEventListener('scroll', requestPos)
      window.removeEventListener('resize', requestPos)
    }
  }, [containerRef, stickyTop])

  return (
    <button
      ref={tabRef}
      type="button"
      className={`hfse-mapctl-btn hfse-mapctl-showtab hfse-mapctl-tip${inView ? ' hfse-mapctl-showtab-inview' : ''}`}
      aria-label="Show map"
      onClick={toggleMap}
    >
      <MapChevronsIcon />
      <MapGlyphIcon />
    </button>
  )
}
